import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Gift, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { sendLeadEmail } from "../../lib/api";

const STORAGE_KEY = "ds_exit_intent_shown_v1";

export const ExitIntent = ({ onCTA }) => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem(STORAGE_KEY) === "1") return;

    let armed = true;
    let touchStartY = 0;

    const trigger = () => {
      if (!armed) return;
      armed = false;
      sessionStorage.setItem(STORAGE_KEY, "1");
      setOpen(true);
    };

    const onMouseOut = (e) => {
      if (e.clientY <= 0 && !e.relatedTarget) trigger();
    };
    const onTouchStart = (e) => {
      touchStartY = e.touches[0]?.clientY ?? 0;
    };
    const onTouchMove = (e) => {
      // fast upward swipe near top = treat like exit intent on mobile
      const y = e.touches[0]?.clientY ?? 0;
      if (window.scrollY < 80 && touchStartY - y > 80) trigger();
    };
    const onVis = () => {
      if (document.visibilityState === "hidden") trigger();
    };
    const t = setTimeout(() => {
      document.addEventListener("mouseout", onMouseOut);
      document.addEventListener("touchstart", onTouchStart, { passive: true });
      document.addEventListener("touchmove", onTouchMove, { passive: true });
      document.addEventListener("visibilitychange", onVis);
    }, 8000); // arm after 8s

    return () => {
      clearTimeout(t);
      document.removeEventListener("mouseout", onMouseOut);
      document.removeEventListener("touchstart", onTouchStart);
      document.removeEventListener("touchmove", onTouchMove);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !/^\S+@\S+\.\S+$/.test(email)) {
      toast.error("Please enter your name and a valid email.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        full_name: name,
        email,
        phone: "—",
        biggest_challenge: "Requested FREE website audit (exit-intent popup).",
        project_details: "Source: Exit-intent popup. Send free audit + PDF.",
      };
      await sendLeadEmail(payload);
      toast.success("Your free audit is on the way!");
      setOpen(false);
    } catch {
      toast.error("Couldn't submit. Please try the main form.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="exit-intent"
          data-testid="exit-intent-popup"
          className="fixed inset-0 z-[70] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            onClick={() => setOpen(false)}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.94, y: 20 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="relative glass border border-[#D4AF37]/30 rounded-3xl w-full max-w-lg p-7 sm:p-10 noise"
          >
            <button
              data-testid="exit-intent-close"
              onClick={() => setOpen(false)}
              aria-label="Close"
              className="absolute top-4 right-4 w-8 h-8 rounded-full border border-white/10 flex items-center justify-center text-[#B0B0B0] hover:text-white hover:border-white/30 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="inline-flex items-center gap-2 glass-gold rounded-full px-3 py-1 mb-5">
              <Gift className="w-3.5 h-3.5 text-[#D4AF37]" />
              <span className="text-[10px] tracking-[0.2em] uppercase text-[#D4AF37]">
                Wait — one second
              </span>
            </div>

            <h3 className="font-display text-3xl sm:text-4xl text-white tracking-tight leading-tight">
              Before you leave, get a <span className="gold-text italic">free website audit.</span>
            </h3>
            <p className="mt-3 text-sm text-[#B0B0B0]">
              I'll personally review your current site (or your competitor's, if you don't have one)
              and send a short report with the top 5 changes that will bring more leads.
            </p>

            <form onSubmit={onSubmit} className="mt-6 space-y-3">
              <input
                data-testid="exit-intent-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name *"
                className="field-focus w-full bg-transparent border border-[#222] focus:border-[#D4AF37] rounded-xl px-4 py-3 outline-none text-white placeholder:text-[#555] text-sm transition-all"
              />
              <input
                data-testid="exit-intent-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email address *"
                className="field-focus w-full bg-transparent border border-[#222] focus:border-[#D4AF37] rounded-xl px-4 py-3 outline-none text-white placeholder:text-[#555] text-sm transition-all"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="exit-intent-submit"
                className="btn-gold-glow w-full inline-flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#E6C35C] disabled:opacity-60 text-black font-semibold rounded-full px-6 py-3.5 transition-all"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Sending...
                  </>
                ) : (
                  <>
                    Send My Free Audit <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>
            <p className="mt-3 text-[10px] text-[#737373] text-center">
              No spam. Your details only reach Divy.
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
